import React from 'react';
import PropTypes from 'prop-types';

import { useClassName } from '@travpro/hooks';
import { renderToString } from 'react-dom/server';
import { HashLink as Link } from 'react-router-hash-link';

import LinkHandler from 'components/LinkHandler';
import SharingPopup from 'components/SharingPopup';

import './_SearchResult.scss';

import { baseAppId } from 'settings';

/**
 * @typedef {object} SearchResultProps
 * @property {string} title Title to display
 * @property {string} icon Icon to display
 * @property {string} backgroundImage Background image to display
 * @property {boolean} gradient Whether to display a gradient over the background image
 * @property {string} url URL to link
 * @property {Function} onSelect Function to call on select
 */

/**
 * @param {SearchResultProps} props SearchResultProps
 * @returns {JSX.Element} SearchResult
 */
const SearchResult = ({
  title = null,
  description = null,
  icon = null,
  backgroundImage = null,
  gradient = true,
  url = '',
  type = '',
  hitType = '',
  onSelect = () => {},
}) => {
  const c = useClassName('search-result');

  const checkedUrl = url || '#';
  const fancyOptions = {
    infobar: true,
    thumbs: {
      autoStart: true,
      axis: 'x',
    },
    baseClass: 'customBox',
    buttons: ['thumbs', 'share', 'close'],
    share: {
      tpl: renderToString(<SharingPopup url={checkedUrl} image={backgroundImage} />),
    },
  };

  if (type === 'sales_companion_items') {
    return (
      <span onClick={onSelect} role="button" tabIndex="0" onKeyDown={onSelect}>
        <LinkHandler
          appId={baseAppId}
          url={checkedUrl}
          type={hitType}
          title={title}
          UniqueID={checkedUrl}
          dataOptions={JSON.stringify(fancyOptions)}
          dataFancybox={checkedUrl}
          dataTrigger={checkedUrl}
          page="f"
        >
          <div {...c({ small: !backgroundImage })}>
            {icon && <img {...c('icon', { small: !backgroundImage })} src={icon} alt={`icon: ${title}`} />}

            {title && <p {...c('title', { small: !backgroundImage })}>{title}</p>}

            {backgroundImage && <img {...c('background')} src={backgroundImage} alt={`background: ${title}`} />}

            {gradient === true && backgroundImage && <div {...c('gradient')} />}
          </div>
        </LinkHandler>
      </span>
    );
  }

  return (
    <Link to={checkedUrl} onClick={onSelect}>
      <div {...c({ small: !backgroundImage })}>
        {backgroundImage && <img {...c('background')} src={backgroundImage} alt={`background: ${title}`} />}
        {!backgroundImage && <div {...c('background', 'background--fallback')} />}
        {gradient === true && backgroundImage && <div {...c('gradient')} />}

        {title && <p {...c('title', { small: !backgroundImage })}>{title}</p>}

        {description && description.props.hit.text.length > 10 && (
          <p {...c('description', { small: !backgroundImage })}>{description}</p>
        )}

        {icon && <img {...c('icon', { small: !backgroundImage })} src={icon} alt="search-result icon" />}
      </div>
    </Link>
  );
};

export default SearchResult;

// Title and description can be either a string or an element. This is to allow for more complex search results, such as highlighted text, using Algolia Search components
SearchResult.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  icon: PropTypes.string,
  backgroundImage: PropTypes.string,
  gradient: PropTypes.bool,
  url: PropTypes.string,
  onSelect: PropTypes.func,
  type: PropTypes.string,
  hitType: PropTypes.string,
};
